import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Trash2, RefreshCw, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import OtpDialog, { useOtp } from "@/components/OtpDialog";

// One API-key card (status, replace, test, remove). Every change needs an emailed code.
export default function KeyCard({ provider, title, subtitle, icon: Icon, iconClass, link, extra, onLocked, buttonClass = "" }) {
  const sfx = provider === "sume" ? "" : `-${provider}`;
  const [st, setSt] = useState(null);
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState("");
  const fail = (e, fb) => { if (e?.response?.status === 423) onLocked(); else toast.error(errMsg(e, fb)); };
  useEffect(() => { api.get(`/settings/${provider}`).then((r) => setSt(r.data)).catch((e) => fail(e, "Could not load key status")); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  const o = useOtp(`/settings/${provider}/otp`, async (otp, code) => {
    const body = { challenge_id: otp.challenge_id, code };
    try {
      const { data } = otp.action === "save" ? await api.put(`/settings/${provider}`, { value, ...body }) : await api.post(`/settings/${provider}/remove`, body);
      setSt(data);
      if (otp.action === "save") setValue("");
      toast.success(otp.action === "save" ? `${title} updated` : "Key removed");
      return true;
    } catch (e) { fail(e, "Request failed"); return false; }
  });

  const test = async () => {
    setBusy("test");
    try { const { data } = await api.post(`/settings/${provider}/test`); setSt(data); data.status === "error" ? toast.error(data.error) : toast.success("Connection OK"); }
    catch (e) { fail(e, "Test failed"); } finally { setBusy(""); }
  };
  const connected = st?.configured && st?.status === "connected";

  return (
    <section className="ax-panel p-6 mt-6" data-testid={`settings-card-${provider}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${iconClass}`}><Icon className="w-4 h-4" /></div>
          <div><div className="font-heading text-lg font-semibold">{title}</div><div className="text-xs text-zinc-500">{subtitle}</div></div>
        </div>
        <span data-testid={`settings-status-${provider}`}
          className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full border ${connected ? "text-[#7AF25F] border-[#7AF25F]/30 bg-[#7AF25F]/10" : "text-zinc-400 border-zinc-700 bg-zinc-800/40"}`}>
          {connected ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}{connected ? "Connected" : "Not Connected"}
        </span>
      </div>
      {st?.configured && (
        <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
          <div className="rounded-lg bg-[#0D0D0F] border border-zinc-800 p-3">
            <div className="text-zinc-500 font-mono uppercase tracking-widest text-[10px]">Current key</div>
            <div className="font-mono text-zinc-200 mt-1" data-testid={`settings-masked-${provider}`}>{st.masked}</div>
            {st.source === "environment" && <div className="text-zinc-600 mt-0.5">from server config</div>}
          </div>
          {extra?.(st)}
        </div>
      )}
      {st?.status === "error" && st.error && <p className="mt-3 text-xs text-red-400" data-testid={`settings-error-${provider}`}>{st.error}</p>}
      <div className="mt-5 flex items-center gap-2">
        <div className="flex-1 flex items-center bg-[#0D0D0F] border border-zinc-800 rounded-lg focus-within:border-[#5FC0C8]/60">
          <input type={show ? "text" : "password"} value={value} onChange={(e) => setValue(e.target.value)}
            placeholder={st?.configured ? "Paste a new key to replace the current one" : `Paste your ${title}`}
            data-testid={`settings-input-${provider}`} className="flex-1 bg-transparent outline-none text-sm font-mono px-3 py-2.5 placeholder:text-zinc-600" />
          <button type="button" onClick={() => setShow((s) => !s)} data-testid={`settings-toggle-${provider}`} className="px-3 text-zinc-500 hover:text-white" aria-label="Show or hide key">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <button onClick={() => (value.trim() ? o.ask("save") : toast.error("Paste a key first"))} disabled={o.busy} data-testid={`settings-save-${provider}`} className={`ax-generate h-11 px-6 ${buttonClass}`}>
          {o.busy && !o.otp ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </button>
      </div>
      <a href={link.href} target="_blank" rel="noreferrer" className="text-[11px] text-zinc-500 hover:text-white mt-2 inline-flex items-center gap-1">{link.label} <ExternalLink className="w-3 h-3" /></a>
      {st?.configured && (
        <div className="mt-5 pt-4 border-t border-zinc-800 flex items-center gap-5">
          <button onClick={test} disabled={!!busy} data-testid={`settings-test-${provider}`} className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-1.5">
            {busy === "test" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Test connection
          </button>
          {st.source === "settings" && (
            <button onClick={() => o.ask("remove")} disabled={o.busy} data-testid={`settings-remove-${provider}`} className="text-xs font-mono uppercase tracking-widest text-red-400/80 hover:text-red-400 flex items-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          )}
        </div>
      )}
      <OtpDialog o={o} testid={`settings-otp${sfx}`} title={`Approve ${o.otp?.action === "remove" ? "key removal" : "key change"} · ${title}`} />
    </section>
  );
}
